import { AnalyticsBrowser } from '@segment/analytics-next';
import posthog from 'posthog-js';

const analytics = AnalyticsBrowser.load(
  {
    writeKey: process.env.REACT_APP_SEGMENT_API_KEY || '',
  },
  {
    cookie: { domain: '.banca.me' },
  },
);

if (typeof window !== 'undefined' && process.env.REACT_APP_POSTHOG_API_KEY) {
  posthog.init(
    process.env.REACT_APP_POSTHOG_API_KEY,
    {
      api_host: process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com',
      segment: {
        user: async () => {
          const user = await analytics.user();
          return {
            id: () => user.id() ?? undefined,
            traits: () => user.traits(),
            anonymousId: () => {
              const anonId = user.anonymousId();
              return typeof anonId === 'string' ? anonId : undefined;
            },
          };
        },
        register: () => Promise.resolve(),
      },
      person_profiles: 'identified_only',
      session_recording: {
        recordCrossOriginIframes: true,
      },
    },
  );
}

let lastPage: { pageCategory: string, pageName: string } | null = null;

analytics.addSourceMiddleware(async ({ payload, next }) => {
  try {
    const updatedPayload = payload as any;
    const payloadType = payload.type();
    if (lastPage) {
      if (payloadType === 'page') {
        updatedPayload.obj.properties = {
          ...(payload.obj.properties || {}),
          prevPageName: lastPage.pageName,
          prevPageCategory: lastPage.pageCategory,
        };
      } else if (payloadType === 'track') {
        updatedPayload.obj.properties = {
          ...(payload.obj.properties || {}),
          ...lastPage,
        };
      }
    }
    if (payloadType === 'page') {
      const { properties } = payload.obj;
      lastPage = { pageCategory: properties?.category, pageName: properties?.name };
    }
    const user = await analytics.user();
    const traits = user.traits();

    if (traits && Object.keys(traits).length > 0) {
      updatedPayload.obj.properties = {
        ...(updatedPayload.obj.properties || {}),
        ...traits,
      };
      return next(updatedPayload);
    }

    return next(updatedPayload);
  } catch (error) {
    return next(payload);
  }
});

export const addPreApproveId = async (id: string) => {
  const user = await analytics.user();
  const traits = user.traits();
  if (traits?.preApproveId !== id) {
    analytics.identify(undefined, { preApproveId: id });
  }
};

export const identifyHandler = async (plrId: string, identificationId: string) => {
  const user = await analytics.user();
  const userId = user.id();
  if (!userId || userId?.toString() !== identificationId) {
    return analytics.identify(identificationId, { plrId });
  }
  const prevPlrId = user.traits()?.plrId;
  if (prevPlrId !== plrId) {
    return analytics.identify(undefined, { plrId });
  }
  return undefined;
};

export const identifyAuthHandler = async (identificationId: string, detailId?: string, type: 'pre-approve' | 'loan-request' = 'pre-approve') => {
  const user = await analytics.user();
  const userId = user.id();
  if (!userId || userId?.toString() !== identificationId) {
    if (type === 'pre-approve') {
      return analytics.identify(identificationId, {
        preApproveId: detailId,
        loanRequestId: undefined,
      });
    }
    return analytics.identify(identificationId, { loanRequestId: detailId });
  }
  const traits = user.traits();
  if (traits?.preApproveId === detailId || traits?.loanRequestId === detailId) {
    return undefined;
  }
  if (type === 'pre-approve') {
    return analytics.identify(undefined, {
      preApproveId: detailId,
      loanRequestId: undefined,
    });
  }
  return analytics.identify(undefined, { loanRequestId: detailId });
};

export const resetAnalytics = async () => {
  const user = await analytics.user();
  if (!user.id()) {
    return;
  }
  const traits = user.traits();
  if (traits && Object.keys(traits).length > 0) {
    const newEmptyTraits: { [key: string]: any } = {};
    Object.keys(traits).forEach((key) => {
      newEmptyTraits[key] = undefined;
    });
    await analytics.identify(undefined, newEmptyTraits);
  }
  if (process.env.REACT_APP_POSTHOG_API_KEY) {
    posthog.reset();
  }
};

export { posthog };
export default analytics;
